/**
 * View component for /certificate-templates
 *
 * Generic certificate-template list view. Defaults to 'all' with:
 * this.props.dispatch(certificateTemplateActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as certificateTemplateActions from '../certificateTemplateActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import certificate-template components
import AdminCertificateTemplateListItem from '../components/AdminCertificateTemplateListItem.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';


class AdminCertificateTemplateList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(certificateTemplateActions.fetchListIfNeeded()); // defaults to 'all'
    dispatch(certificateTemplateActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(certificateTemplateActions.setPagination({page: 1, per: 50}));
    dispatch(contentModuleActions.fetchListIfNeeded());
  }

  _handleSort(sortBy) {
    const { dispatch, match, certificateTemplateList } = this.props;
    let newFilter = certificateTemplateList.filter;
    if(certificateTemplateList.filter.sortBy && certificateTemplateList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(certificateTemplateActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(certificateTemplateActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, certificateTemplateList } = this.props;
    let newPagination = certificateTemplateList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(certificateTemplateActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(certificateTemplateActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, certificateTemplateList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = certificateTemplateList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(certificateTemplateActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: certificateTemplateList.filter.sortBy
    }));
  }

  render() {
    const {
      certificateTemplateList
      , certificateTemplateStore
      , location
      , match
      , paginatedList
      , sortedList
      , contentModuleStore
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    const contentModuleList = contentModuleStore.util.getList() || [];

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !certificateTemplateList || !certificateTemplateList.items || certificateTemplateList.didInvalidate;
    const sortedEmpty = sortedList.length === 0;

    return (
      <div className="flex">
        <section className="section">
          <div className="yt-container">
            <h1> CertificateTemplate List
              <Link to={match.url + '/new'} className="yt-btn create u-pullRight">
                <img className="-icon" src={'/img/create-white.png'}></img>
              </Link>
              </h1>
            <hr/>
            { isEmpty ?
                (
                  certificateTemplateList && certificateTemplateList.isFetching ?
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                  :
                    <h2>Empty.</h2>
                )
              :
                <div style={{ opacity: certificateTemplateList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={certificateTemplateList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                          { certificateTemplateList.filter && certificateTemplateList.filter.query && certificateTemplateList.filter.query.length > 0 ?
                            <span>Filtered CertificateTemplates &mdash; {sortedList.length} of {Object.keys(certificateTemplateList.items).length}</span>
                            :
                            <span>All CertificateTemplates &mdash; {Object.keys(certificateTemplateList.items).length}</span>
                          }
                          <div className="per-page-select u-pullRight">
                            <label>Show per page: </label>
                            <select
                              name="numPerPage"
                              onChange={(e) => this._setPerPage(e.target.value)}
                              value={this.state.perPage}
                              >
                              <option value={25}> 25 </option>
                              <option value={50}> 50 </option>
                              <option value={100}> 100 </option>
                            </select>
                          </div>
                        </caption>
                        <thead>
                          <tr>
                            <th className="_40 sortable" onClick={()=> this._handleSort('name')}>
                              Name
                              {certificateTemplateList.filter.sortBy === 'name' ?
                                <i className="ion ion-ios-arrow-down" />
                                : certificateTemplateList.filter.sortBy === '-name' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            
                            <th className="_20 sortable right" onClick={()=> this._handleSort('updated')}>
                              Last Modified
                              {certificateTemplateList.filter.sortBy === 'updated' ?
                                <i className="ion ion-ios-arrow-down" />
                                : certificateTemplateList.filter.sortBy === '-updated' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((certificateTemplate, i) =>
                            <AdminCertificateTemplateListItem
                              certificateTemplate={certificateTemplate}
                              key={certificateTemplate._id}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={certificateTemplateList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / certificateTemplateList.pagination.per) + 1}
                        />
                    }
                  </div>
                  </div>
                </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

AdminCertificateTemplateList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const certificateTemplateStore = store.certificateTemplate;
  const certificateTemplateList = certificateTemplateStore.lists ? certificateTemplateStore.lists.all : null;
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (certificateTemplateList) {

    const pagination = certificateTemplateList.pagination;
    const filter = certificateTemplateList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = certificateTemplateList && certificateTemplateList.items ? certificateTemplateList.items.filter((certificateTemplateId) => {
      return filterUtils.filterCertificateTemplate(queryTestString, certificateTemplateStore.byId[certificateTemplateId]);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = certificateTemplateStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    certificateTemplateList
    , certificateTemplateStore
    , paginatedList
    , sortedList
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminCertificateTemplateList)
);



// WEBPACK FOOTER //
// modules/certificateTemplate/views/AdminCertificateTemplateList.js.jsx